import { Component, computed, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { KENDO_GRIDLAYOUT, KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { AgreementDetailedDto, AgreementsClient, FormSchedulesClient, UnityScheduleType, UnityWorkflowState } from '@unity/clients';
import { AuthorizeDirective } from '@unity/directives/authorize.directive';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { CollapseStateService } from '@unity/services/collapse-state.service';
import { PermissionService } from '@unity/services/permission.service';
import { SessionService } from '@unity/services/session.service';
import { AgreementSelectorSharedComponent } from '@unity/shared/agreement-selector-shared/agreement-selector-shared.component';
import { CardBudgetSummarySharedComponent } from '@unity/shared/card-budget-summary-shared/card-budget-summary-shared.component';
import { CardFundsDisbursedSummarySharedComponent } from '@unity/shared/card-funds-disbursed-summary-shared/card-funds-disbursed-summary-shared.component';
import { CardItemCountSummarySharedComponent } from '@unity/shared/card-item-count-summary-shared/card-item-count-summary-shared.component';
import { CardRemainingAwardSummarySharedComponent } from '@unity/shared/card-remaining-award-summary-shared/card-remaining-award-summary-shared.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { LinkSharedComponent } from '@unity/shared/composed/link-shared/link-shared.component';
import { BannerAlertSharedComponent } from '@unity/shared/presentation/banner-alert-shared/banner-alert-shared.component';
import { QuickActions } from '@unity/utils/quick-actions';
import { pluralize } from '@unity/utils/string-transform';
import { Paging } from 'gridify-client';
import { DateTime } from 'luxon';
import { Observable } from 'rxjs';

@Component({
  selector: 'unity-view-agreement-page',
  standalone: true,
  imports: [
    KENDO_STACKLAYOUT,
    KENDO_GRIDLAYOUT,
    ReactiveFormsModule,
    PageContainerComponent,
    AuthorizeDirective,
    BannerAlertSharedComponent,
    LinkSharedComponent,
    CardItemCountSummarySharedComponent,
    CardFundsDisbursedSummarySharedComponent,
    CardRemainingAwardSummarySharedComponent,
    ActionSharedComponent,
    AgreementSelectorSharedComponent,
    CardBudgetSummarySharedComponent,
  ],
  providers: [CollapseStateService],
  templateUrl: './view-agreement-page.component.html',
  styleUrl: './view-agreement-page.component.scss',
})
export class ViewAgreementPageComponent {
  private readonly agreementsClient = inject(AgreementsClient);
  private readonly permissionService = inject(PermissionService);
  private readonly formSchedulesClient = inject(FormSchedulesClient);
  private readonly sessionService = inject(SessionService);

  public readonly $agreementId = input.required({ alias: 'agreementId', transform: (value: string) => parseInt(value) });

  protected readonly $showActionCategoryLabels = computed(() => {
    const managementActions = this.$managementActions();
    return managementActions.length > 0;
  });

  protected readonly $managementActions = computed(() => {
    const agreement = this.agreementResource.value();

    if (!agreement) {
      return [];
    }

    const actions = [
      QuickActions.ManageAgreement(agreement.id),
      QuickActions.CreateAgreementUser(agreement.id),
      QuickActions.ManageAgreementUsers(agreement.id),
    ];

    if (agreement.supports_documents) {
      actions.push(QuickActions.CreateDocument(agreement.id));
    }

    if (agreement.supports_forms) {
      actions.push(QuickActions.CreateFormSchedule(agreement.id), QuickActions.ManageFormTemplates(agreement.id));
    }

    if (agreement.supports_reference_files) {
      actions.push(QuickActions.CreateReferenceFile(agreement.id));
    }

    return actions.filter(
      x =>
        this.permissionService.userIsEntitled(x.authorize) &&
        (x.authorizeOneOf === undefined || this.permissionService.userIsEntitledToOneOf(x.authorizeOneOf))
    );
  });

  protected readonly $actions = computed(() => {
    const agreement = this.$agreement();
    const formSchedules = this.$formSchedules();

    if (!agreement || !formSchedules) {
      return [];
    }

    const actions = [];

    if (agreement.supports_clients) {
      actions.push(QuickActions.ListClients(agreement.id));
      actions.push(QuickActions.CreateClient(agreement.id));
    }

    if (agreement.supports_client_expenses) {
      actions.push(QuickActions.ListClientExpenses(agreement.id));
      actions.push(QuickActions.CreateClientExpense(agreement.id));
    }

    if (agreement.supports_disbursements) {
      actions.push(QuickActions.ListDisbursement(agreement.id));
      actions.push(QuickActions.CreateDisbursement(agreement.id));
    }

    if (agreement.supports_documents || agreement.supports_reference_files) {
      actions.push(QuickActions.ListDocumentAndReferenceFiles(agreement.id));
    }

    if (agreement.supports_forms) {
      for (const formSchedule of formSchedules.data) {
        if (formSchedule.type_enum_value === UnityScheduleType.OneTime) {
          actions.push(QuickActions.ListFormsForFromSchedule(agreement.id, formSchedule.id, formSchedule.name));
        } else {
          actions.push(QuickActions.ListFormsForFromSchedule(agreement.id, formSchedule.id, pluralize(formSchedule.name)));
        }
        if (formSchedule.type_enum_value === UnityScheduleType.Adhoc) {
          actions.push(QuickActions.CreateFormForFormSchedule(agreement.id, formSchedule.id, pluralize(formSchedule.name)));
        }
      }
    }

    return actions.filter(
      x =>
        this.permissionService.userIsEntitled(x.authorize) &&
        (x.authorizeOneOf === undefined || this.permissionService.userIsEntitledToOneOf(x.authorizeOneOf))
    );
  });

  private agreementResource = rxResource({
    params: () => {
      const agreementId = this.$agreementId();
      return agreementId ? { agreementId } : undefined;
    },
    stream: ({ params: { agreementId } }) => this.agreementsClient.getOneAsDetailed(agreementId),
  });

  private formSchedulesResource = rxResource({
    params: () => {
      const agreementId = this.$agreementId();
      return agreementId ? { agreementId } : undefined;
    },
    stream: ({ params: { agreementId } }) => this.formSchedulesClient.getAllForAgreementAsMini(agreementId),
  });

  private agreementStatsResource = rxResource({
    params: () => {
      const agreementId = this.$agreementId();
      return agreementId ? { agreementId } : undefined;
    },
    stream: ({ params: { agreementId } }) => this.agreementsClient.getOneAsStats(agreementId),
  });

  private assignedAgreementsResource = rxResource({
    params: () => {
      const user = this.sessionService.$user();
      const userId = user.user_id;
      const pages = 1;
      const pageSize = 10;
      const orderBy = 'LastModifiedDt desc';
      return userId ? { userId, pages, pageSize, orderBy } : undefined;
    },
    stream: ({ params: { userId, pages, pageSize, orderBy } }) =>
      this.agreementsClient.getAllForUserAsDetailed(userId, pages, pageSize, orderBy) as unknown as Observable<
        Paging<AgreementDetailedDto>
      >,
  });

  protected $resourceLoading = this.agreementResource.isLoading;
  protected $statsLoading = this.agreementStatsResource.isLoading;
  protected $assignedAgreementsIsLoading = this.assignedAgreementsResource.isLoading;
  protected readonly $assignedAgreements = computed(() => {
    if (!this.assignedAgreementsResource.hasValue()) {
      return undefined;
    }
    return this.assignedAgreementsResource.value().data;
  });
  protected readonly $agreement = computed(() => {
    if (!this.agreementResource.hasValue()) {
      return undefined;
    }
    return this.agreementResource.value();
  });
  protected readonly $agreementStats = computed(() => {
    if (!this.agreementStatsResource.hasValue()) {
      return undefined;
    }
    return this.agreementStatsResource.value();
  });
  protected readonly $formSchedules = computed(() => {
    if (!this.formSchedulesResource.hasValue()) {
      return undefined;
    }
    return this.formSchedulesResource.value();
  });
  protected readonly $isDraft = computed(() => this.agreementResource.value()?.current_state.enum_value == UnityWorkflowState.Draft);
  protected readonly $isDisabled = computed(() => this.agreementResource.value()?.current_state.enum_value == UnityWorkflowState.Disabled);
  protected readonly $overdueFormCount = computed(() => this.agreementStatsResource.value()?.overdue_form_count);

  protected readonly $contentHeaderSecondary = computed(() => `Program: ${this.agreementResource.value()?.program.name}`);
  protected readonly $contentHeaderTertiary = computed(() => {
    const agreement = this.agreementResource.value();
    if (!agreement) {
      return undefined;
    }
    const startDate = DateTime.fromJSDate(agreement?.start_date).toFormat('MMM dd, yyyy');
    const endDate = DateTime.fromJSDate(agreement?.end_date).toFormat('MMM dd, yyyy');

    return `Period: ${startDate} - ${endDate}`;
  });
}
